import { type DocumentNode } from '@apollo/client';
import {
  Button,
  ButtonColor,
  type ButtonProps,
  ButtonSize,
  compact,
  formatDate,
  formatTime,
  IconCancel,
  IconDownload,
  IconLockFilled,
  IconPencil,
  IconRecurring,
  IconSparkle,
  type MenuItemSection,
  Tooltip,
} from '@leland-dev/leland-ui-library';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useCallback, useMemo } from 'react';

import { getClassUrl } from '../../utils/classes';
import { getCTATooltipTextForBlockedUser } from '../../utils/coach';
import {
  CoachingSessionUrgencyEnum,
  getCoachingSessionMeetingUrl,
  useCoachingSessionTimeRemaining,
} from '../../utils/coaching-session';
import { APPLICANT_SITE_URL } from '../../utils/constants';
import {
  isSessionClassOrFreeEvent,
  isSessionOneOnOne,
} from '../../utils/session';
import ThreeDotMenu from '../common/ThreeDotMenu';

import { useSessionDetailsModal } from './SessionDetailsModal';
import {
  type CoachingSessionCoachingSessionFragment,
  useCoachingSessionCreateHourlyOrderMutation,
} from './__generated-gql-types__/CoachingSession.generated';
import { useCancelCoachingSessionModal } from './modals/ScheduleModal/CancelCoachingSessionModal';

type CoachingSessionProps = {
  session: CoachingSessionCoachingSessionFragment;
  blocked?: boolean;
  refetchQueries?: DocumentNode[];
} & (
  | {
      isPast: true;
      onReschedule?: never;
    }
  | {
      isPast?: false;
      onReschedule: () => void;
    }
);

const CoachingSession: React.FC<CoachingSessionProps> = ({
  session,
  onReschedule,
  isPast,
  blocked,
  refetchQueries,
}) => {
  const router = useRouter();
  const {
    setOpen: setCancelCoachingSessionModalOpen,
    Modal: CancelCoachingSessionModal,
  } = useCancelCoachingSessionModal();

  const { setOpen: setSessionDetailsModalOpen, Modal: SessionDetailsModal } =
    useSessionDetailsModal();
  const [createOrder, { loading: createOrderLoading }] =
    useCoachingSessionCreateHourlyOrderMutation();

  const sessionContent = session.content;

  const date = formatDate(session.startAt);

  const timeRange = `${formatTime(session.startAt)} - ${formatTime(
    session.endAt,
  )}`;

  const blockedUserCTAText = getCTATooltipTextForBlockedUser(
    session.coach.user.firstName,
  );

  const { urgency, remainingTime } = useCoachingSessionTimeRemaining(
    session.startAt,
    session.endAt,
  );

  const menuItems: MenuItemSection = useMemo(
    () =>
      [
        ...(isPast
          ? []
          : isSessionOneOnOne(sessionContent)
            ? [
                onReschedule
                  ? {
                      label: 'Reschedule Session',
                      onSelect: onReschedule,
                      LeftIcon: IconPencil,
                      disabled: blocked,
                    }
                  : null,
                {
                  label: 'Cancel Session',
                  onSelect: () => setCancelCoachingSessionModalOpen(true),
                  LeftIcon: IconCancel,
                },
              ]
            : isSessionClassOrFreeEvent(sessionContent)
              ? [
                  {
                    label: `View ${sessionContent.isFreeEvent ? 'event' : 'class'}`,
                    onSelect: () =>
                      window.open(
                        getClassUrl(sessionContent.slug ?? sessionContent.id),
                        '_blank',
                        'noopener, noreferrer',
                      ),
                  },
                ]
              : []), // TODO: Update for bootcamp sessions

        session.hasRecording
          ? {
              label: 'Download Recording',
              onSelect: () =>
                window.open(
                  `${APPLICANT_SITE_URL}/recording/${session.id}`,
                  '_blank',
                ),
              LeftIcon: IconDownload,
            }
          : null,
        session.summary
          ? {
              label: 'View Summary',
              onSelect: () => setSessionDetailsModalOpen(true),
              LeftIcon: IconSparkle,
            }
          : null,
      ].filter(compact),
    [
      isPast,
      blocked,
      onReschedule,
      session.hasRecording,
      session.id,
      session.summary,
      sessionContent,
      setCancelCoachingSessionModalOpen,
      setSessionDetailsModalOpen,
    ],
  );

  const onPayOrder = useCallback(async () => {
    const { data } = await createOrder({
      variables: {
        coachUrn: session.coach.id,
        timeInMinute: session.additionalTimeToBuy,
      },
    });
    await router.push('/checkout?order=' + data?.createOrder.id);
  }, [createOrder, session.coach.id, session.additionalTimeToBuy, router]);

  const renderButton = (onClick?: ButtonProps['onClick']) => {
    const buttonProps: ButtonProps = {
      label: session.additionalTimeToBuy
        ? createOrderLoading
          ? 'Paying...'
          : 'Pay for session'
        : urgency === CoachingSessionUrgencyEnum.NOW
          ? 'Join session'
          : urgency === CoachingSessionUrgencyEnum.UPCOMING
            ? `Join session ${remainingTime}`
            : `Starts in ${remainingTime}`,
      buttonColor: ButtonColor.BLUE,
      size: ButtonSize.SMALL,
      disabled: blocked || createOrderLoading,
      tooltipContent: blocked ? blockedUserCTAText : null,
      ...(onClick ? { onClick } : {}),
    };
    return <Button {...buttonProps} />;
  };

  return (
    <>
      <div className="rounded-lg bg-leland-gray-hover p-3">
        <div className="flex items-center justify-between space-x-4">
          <div className="flex items-start justify-start gap-1.5">
            <p
              className={`max-w-60 font-medium ${
                session.canceled
                  ? 'text-leland-gray-light line-through'
                  : 'text-leland-gray-dark'
              }`}
            >
              {isSessionOneOnOne(sessionContent)
                ? sessionContent.title
                : isSessionClassOrFreeEvent(sessionContent)
                  ? `${sessionContent.isFreeEvent ? 'Event' : 'Class'}${sessionContent.name ? `: ${sessionContent.name}` : ''}`
                  : sessionContent.bootcamp.title}
            </p>
            {session.recurringSession ? (
              <Tooltip
                content={`Repeats ${session.recurringSession.formattedCadence}`}
              >
                <IconRecurring className="mt-1 size-4" />
              </Tooltip>
            ) : null}
          </div>
          {menuItems.length > 0 ? (
            <ThreeDotMenu itemSections={[menuItems]} />
          ) : null}
          {session.canceled ? (
            <p className="text-leland-gray-extra-light">Canceled</p>
          ) : null}
        </div>
        <div
          className={`flex items-center justify-between space-x-4 ${
            isPast ? 'mt-1' : 'mt-2.5'
          }`}
        >
          <div
            className={`flex leading-tight text-leland-gray-light ${
              isPast ? 'space-x-2' : 'flex-col'
            }`}
          >
            <p>{date}</p>
            <p>{timeRange}</p>
          </div>
          {isPast ? null : session.additionalTimeToBuy ? (
            renderButton(() => void onPayOrder())
          ) : (
            <Link
              href={getCoachingSessionMeetingUrl(session.id)}
              target="_blank"
              rel="noreferrer"
              id="coaching_session--join_or_buy_session"
            >
              {renderButton()}
            </Link>
          )}
        </div>
        {session.additionalTimeToBuy ? (
          <div className="mt-2 flex flex-row justify-center rounded-lg bg-leland-gray-stroke p-1.5">
            <IconLockFilled className="mr-1 size-3.5 place-self-center text-leland-gray-light" />
            <p className="text-sm font-medium text-leland-gray-light">
              Insufficient balance for this session
            </p>
          </div>
        ) : null}
      </div>

      {session.summary ? (
        <SessionDetailsModal summary={session.summary} />
      ) : null}

      <CancelCoachingSessionModal
        session={session}
        refetchQueries={refetchQueries}
      />
    </>
  );
};

export default CoachingSession;
